import React, { useState, useEffect, useContext } from "react";
import ActionsContext from "common/ActionsContext";
import styled from "styled-components";
import Request from "common/Request";
import Modal from "common/Modal";
import { getIndexes } from "common/utils";
import BqnContext from "common/BqnContext";
import { getBqnIdCmdPart } from "common/api";
import Table from "common/graphs/Table/Table";
import { atoiv6, atoiv4 } from "common/utils";
import { NumberCell } from "common/graphs/Table/TableRenders";

const doesNothing = () => {};

const StatusLine = ({ status, bqn }) => {
  const { bqnId } = useContext(BqnContext);
  return (
    <div className="m-t-10 m-b-15">
      <input type="radio" className="radio-col-red" checked={true} />
      <label style={{ cursor: "default" }}>
        <b>
          {status === "warning"
            ? `No NTP servers are synchronizing the date${
                bqnId === "all" ? ` in BQN ${bqn}` : ""
              }`
            : status === "notice"
              ? "No NTP servers configured"
              : ""}
        </b>
      </label>
    </div>
  );
};

const NTPStatus = ({ status, bqn }) => {
  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const [header, ...rows] = status.trim().split("\n");
  const headerIndex = getIndexes(header);
  const statusPerBqn = rows.reduce((acc, row) => {
    const rowAttrs = row.trim().split(/\s+/);
    const bqn = rowAttrs[headerIndex["BQN"]];
    if (
      mode === "standalone" ||
      bqnId === "all" ||
      parseFloat(bqn) === parseFloat(bqnId)
    ) {
      const status = rowAttrs[headerIndex["STATUS"]];
      acc[bqn] = status;
    }
    return acc;
  }, {});

  return Object.keys(statusPerBqn).map((bqn) => {
    const status = statusPerBqn[bqn];
    return status !== "warning" && status !== "notice" ? null : (
      <StatusLine status={status} key={bqn} bqn={bqn} />
    );
  });
};

const getColumns = (mode, setDeleteSelected, setShowDeleteModal) => {
  return [
    ...(mode !== "standalone"
      ? [
          {
            label: "BQN",
            id: "BQN",
            numeric: true,
          },
        ]
      : []),
    {
      label: "STATUS",
      id: "TA",
      numeric: false,
    },
    {
      label: "SERVER",
      id: "REMOTE",
      numeric: false,
      parse: (value) => {
        if (!value) {
          return 0;
        }
        return value.includes(":") ? atoiv6(value) : atoiv4(value);
      },
      parseFilter: (value) => value,
    },
    {
      label: "REFID",
      id: "REFID",
      numeric: false,
    },
    {
      label: "STRATUM",
      id: "ST",
      numeric: true,
      renderCell: (value) => (
        <NumberCell value={value} hasNA={false} precision={0} />
      ),
    },
    {
      label: "TYPE",
      id: "T",
      numeric: false,
    },
    {
      label: "WHEN",
      id: "WHEN",
      numeric: true,
      renderCell: (value) => (
        <NumberCell
          value={value === "-" ? "n/a" : value}
          hasNA={true}
          precision={0}
        />
      ),
      parse: (value) => (value === "-" ? "n/a" : value),
    },
    {
      label: "POLL",
      id: "POLL",
      numeric: true,
      renderCell: (value) => (
        <NumberCell value={value} hasNA={false} precision={0} />
      ),
    },
    {
      label: "REACH",
      id: "REACH",
      numeric: true,
      renderCell: (value) => (
        <NumberCell value={value} hasNA={false} precision={0} />
      ),
    },
    {
      label: "DELAY",
      id: "DELAY",
      numeric: true,
      renderCell: (value) => (
        <NumberCell value={value} hasNA={false} precision={0} />
      ),
    },
    {
      label: "OFFSET",
      id: "OFFSET",
      numeric: true,
      renderCell: (value) => (
        <NumberCell value={value} hasNA={false} precision={0} />
      ),
    },
    {
      label: "JITTER",
      id: "JITTER",
      numeric: true,
      renderCell: (value) => (
        <NumberCell value={value} hasNA={false} precision={0} />
      ),
    },
    {
      label: "ACTIONS",
      id: "ACTIONS",
      actions: [
        {
          label: "Delete",
          icon: "delete",
          onClick: (row) => {
            setDeleteSelected([row["REMOTE"]]);
            setShowDeleteModal(true);
          },
        },
      ],
    },
  ];
};

const reportIfFailsDuring = (action, process) =>
  process
    .then((response) => {
      if (response) {
        console.log("server.<%s>.error: <%s>", action, response);
        throw `Warning: ${response}`;
      }
    })
    .catch(window.showModalError);

const DeleteModalWrapper = styled.div`
  ul {
    padding: 0 0 0 15px;
    margin: -20px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
    }
  }
`;

const DeleteModal = ({
  showDeleteModal,
  setShowDeleteModal,
  items = [],
  whenDone,
}) => {
  const handleApply = () => {
    const cmds = items.map((server) => `clear service ntp server ${server}`);
    reportIfFailsDuring(
      "delete",
      ifCl.run(`${cmds.join("\n")}\n`, /*is batch*/ true),
    ).then(() => {
      setShowDeleteModal(false);
      whenDone();
    });
  };
  if (!showDeleteModal) {
    return null;
  }
  const title =
    items.length === 1
      ? `Delete "${items[0]}" server?`
      : `Delete ${items.length} servers?`;
  return (
    <Modal
      title={title}
      onClose={() => setShowDeleteModal(false)}
      closeLabel="CANCEL"
      superIcon={"delete_forever"}
      large={items.length > 1 && items.length < 100}
      content={() => (
        <DeleteModalWrapper>
          {items.length === 1 ? (
            <p>
              <b>WARNING</b>: The server will be permanently deleted and the NTP
              service will be restarted.
            </p>
          ) : null}
          {items.length > 1 ? (
            <>
              {items.length < 100 ? (
                <ul>
                  {items.map((server) => (
                    <li key={server}>{server}</li>
                  ))}
                </ul>
              ) : null}
              <p>
                <b>WARNING</b>: The servers will be permanently deleted and the
                NTP service will be restarted.
              </p>
            </>
          ) : null}
        </DeleteModalWrapper>
      )}
      applyLabel="YES"
      onApply={handleApply}
    />
  );
};

const NTPServersList = ({ response, send = doesNothing }) => {
  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [selected, setSelected] = useState([]);

  const [header, ...rows] = response.trim().split("\n");
  const headerAttrs = header.trim(/\s/).split(/\s+/);
  const newRows = rows.map((row, index) => {
    const rowAttrs = row.trim(/\s/).split(/\s+/);
    const newEntry = rowAttrs.reduce(
      (acc, attr, i) => {
        acc[headerAttrs[i]] = attr;
        return acc;
      },
      { id: index },
    );
    return newEntry;
  });
  const columns = getColumns(mode, setDeleteSelected, setShowDeleteModal);

  return (
    <>
      <Table
        isSelectable={true}
        isRowSelectable={false}
        data={newRows}
        sort={true}
        columns={columns}
        search={true}
        csvPrefix="ntp-servers-list"
        rowsPerPageNr={50}
        selected={selected}
        setSelected={setSelected}
        deleteSelected={(values) => {
          const selectedItems = newRows
            .filter((row) => values.includes(row.id))
            .map((row) => row["REMOTE"]);
          setDeleteSelected(selectedItems);
          setShowDeleteModal(true);
        }}
      />
      <DeleteModal
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        items={deleteSelected}
        whenDone={() => {
          setSelected([]);
          send("do-load");
        }}
      />
    </>
  );
};

const ListNTPServers = () => {
  const [request, setRequest] = useState(null);
  const actions = useContext(ActionsContext);
  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);

  const sendSignal = (...args) => actions.send(...args);

  const doRequest = () =>
    Promise.all([
      ifCl.run("show system date status").then((response) => response.trim()),
      ifCl.run(`show system ntp${getBqnIdCmdPart(mode, bqnId)}`),
    ]).then(([status, response]) => ({ status, response }));

  const doLoad = () => {
    setRequest(doRequest());
  };

  useEffect(() => {
    doLoad();
    return actions.recv("do-load", doLoad);
  }, [bqnId]);

  return request === null ? null : (
    <Request during={request}>
      {(result) => (
        <>
          <NTPStatus {...result} />
          <NTPServersList {...result} send={sendSignal} />
        </>
      )}
    </Request>
  );
};

export default ListNTPServers;
